import { z } from 'zod';
import { HI_TYPES, type HiType } from '../constants';
import { DATA_CATEGORIES, type DataCategory } from '../roles';

/**
 * What a record holder keeps: a patient's demographics, a FHIR document about
 * the patient, or a pharmacy's record of what it dispensed against one of the
 * patient's prescriptions.
 */
export const RECORD_KINDS = ['patient', 'document', 'dispense'] as const;
export type RecordKind = (typeof RECORD_KINDS)[number];

/** Largest record content one write may carry, leaving room in a response envelope. */
export const MAX_RECORD_BYTES = 160 * 1024;

const accountId = z.string().regex(/^[a-f0-9]{24}$/, 'Must be an account ID');

/**
 * Where a received document came from. patient: the patient's own phone sent
 * it. practice: a practice sent it, as a source the patient named, as a copy
 * for the patient, or as a prescription for a pharmacy. consentId is set for
 * history shares, and forwarded when the patient passed a prescription on to
 * the pharmacy they chose. The sender's own record ID and version let a
 * resend update the copy instead of adding another.
 */
export const recordSourceSchema = z.strictObject({
  consentId: accountId.optional(),
  from: z.enum(['patient', 'practice']),
  practiceId: accountId.optional(),
  practiceName: z.string().max(120).optional(),
  forwarded: z.literal(true).optional(),
  recordId: z.uuid(),
  version: z.number().int().min(1),
  receivedAt: z.iso.datetime(),
});
export type RecordSource = z.infer<typeof recordSourceSchema>;

/**
 * One version of a record kept by a practice's record holder. A patient's
 * demographics record uses the patient's ID as its own ID, and documents
 * point at it with patientId. Every write makes a new version; old versions
 * stay in the store.
 */
export const heldRecordSchema = z.strictObject({
  id: z.uuid(),
  patientId: z.uuid(),
  kind: z.enum(RECORD_KINDS),
  category: z.enum(DATA_CATEGORIES),
  hiType: z.enum(HI_TYPES).optional(),
  version: z.number().int().min(1),
  /** The holder's change counter when this version was saved. */
  seq: z.number().int().min(1),
  /** A FHIR Patient for demographics, or a FHIR document Bundle. */
  resource: z.record(z.string(), z.unknown()),
  /** The request that wrote this version, so a repeated request is answered without writing twice. */
  requestId: z.uuid().optional(),
  /** For demographics: the patient's Curentia account, read from the Patient's identifiers when the practice linked it. */
  accountId: accountId.optional(),
  /** For a document: its own date, read from the document. Timelines and consent date ranges use it. */
  date: z.iso.datetime().optional(),
  /** For a dispensing record: the prescription it answers, by its ID at this holder. */
  prescriptionId: z.uuid().optional(),
  /** For a document received under a patient's consent: where it came from. */
  source: recordSourceSchema.optional(),
  createdAt: z.iso.datetime(),
  createdBy: accountId,
  updatedAt: z.iso.datetime(),
  updatedBy: accountId,
});
export type HeldRecord = z.infer<typeof heldRecordSchema>;

/** The index entry a store keeps for the newest version of each record. */
export type RecordEntry = Omit<HeldRecord, 'resource'>;

/** A record as a writer hands it to the store, before it has a sequence number. */
export type NewRecordVersion = Omit<HeldRecord, 'seq'>;

/**
 * Where a record holder keeps its records: the hub's encrypted files, or the
 * encrypted IndexedDB of a solo doctor's or pharmacist's device. Both encrypt
 * everything they write, and the request engine works the same on either.
 */
export interface RecordStore {
  get(id: string): Promise<HeldRecord | null>;
  /** Entries of the newest versions changed after this sequence number, in sequence order. */
  since(seq: number): Promise<RecordEntry[]>;
  /** Entries of the newest versions of one patient's records, in sequence order. */
  ofPatient(patientId: string): Promise<RecordEntry[]>;
  /** Saves a new version with the next sequence number and returns it. */
  save(record: NewRecordVersion): Promise<HeldRecord>;
}

/** The category of practice data a document belongs to, which decides who may read it. */
export function categoryOfDocument(hiType: HiType): DataCategory {
  return hiType === 'Prescription' ? 'prescriptions' : 'clinical';
}

export function entryOf(record: HeldRecord): RecordEntry {
  const { resource: _resource, ...entry } = record;
  return entry;
}

/** Keeps records in memory only. For tests, and as the reference for the other stores. */
export class MemoryRecordStore implements RecordStore {
  private readonly records = new Map<string, HeldRecord>();
  private seq = 0;

  async get(id: string): Promise<HeldRecord | null> {
    return this.records.get(id) ?? null;
  }

  async since(seq: number): Promise<RecordEntry[]> {
    return [...this.records.values()]
      .filter((record) => record.seq > seq)
      .sort((a, b) => a.seq - b.seq)
      .map(entryOf);
  }

  async ofPatient(patientId: string): Promise<RecordEntry[]> {
    return [...this.records.values()]
      .filter((record) => record.patientId === patientId)
      .sort((a, b) => a.seq - b.seq)
      .map(entryOf);
  }

  async save(record: NewRecordVersion): Promise<HeldRecord> {
    this.seq += 1;
    const saved: HeldRecord = { ...record, seq: this.seq };
    this.records.set(record.id, saved);
    return saved;
  }
}
